import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';

import { Button, Navbar } from '@keboola/design';

import { useAuth } from '../auth/context';

import { AppWordmark } from './AppWordmark';
import { ColorSchemeMenu } from './ColorSchemeMenu';
import { ContactModal } from './ContactModal';

const NAV = [
  { to: '/components', label: 'Components' },
  { to: '/resources', label: 'Resources' },
] as const;

export function PublicChrome({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();

  return (
    <div className="tw:flex tw:min-h-screen tw:flex-col tw:bg-background">
      <Navbar bordered>
        {/* - Must match `DashboardChrome`'s header classes so the logos share an x.
            - ⚠️ `shrink-0`: the slot is `overflow-hidden` and would clip the name first;
              `Navbar.Content` is the part meant to give. */}
        <Navbar.Header className="tw:mr-6 tw:shrink-0 tw:pl-2.5">
          <AppWordmark />
        </Navbar.Header>

        <Navbar.Content>
          <Navbar.Menu>
            {NAV.map((item) => (
              <Navbar.MenuItem key={item.to}>
                <Navbar.MenuButton asChild>
                  {/* No manual `aria-current`: `Link` sets it, non-exact, so detail pages keep it lit. */}
                  <Link to={item.to}>{item.label}</Link>
                </Navbar.MenuButton>
              </Navbar.MenuItem>
            ))}
          </Navbar.Menu>
        </Navbar.Content>

        <Navbar.Footer>
          <ColorSchemeMenu />
          {isAuthenticated ? (
            <Button asChild variant="primary" size="small">
              <Link to="/~">Dashboard</Link>
            </Button>
          ) : (
            <>
              <Button asChild variant="invisible" size="small">
                <Link to="/auth/login">Sign in</Link>
              </Button>
              <Button asChild variant="primary" size="small">
                <Link to="/auth/create-account">Create account</Link>
              </Button>
            </>
          )}
        </Navbar.Footer>
      </Navbar>

      <main className="tw:flex-1 tw:px-6 tw:py-8">
        <div className="tw:mx-auto tw:w-full tw:max-w-6xl">{children}</div>
      </main>

      <footer className="tw:border-t tw:border-border tw:p-6">
        <div className="tw:mx-auto tw:flex tw:w-full tw:max-w-6xl tw:flex-wrap tw:items-center tw:gap-x-6 tw:gap-y-2 tw:text-sm tw:text-muted-foreground">
          <span>© Keboola</span>
          <a
            href="https://help.keboola.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="tw:text-primary tw:underline"
          >
            Documentation
          </a>
          {/* `developers.keboola.com` points here; `apps-api.keboola.com/docs` answers 403. */}
          <a
            href="https://api.keboola.com/?service=developer-portal"
            target="_blank"
            rel="noopener noreferrer"
            className="tw:text-primary tw:underline"
          >
            API reference
          </a>
          {/* Modal-only: there is no `/contact` route. */}
          <ContactModal />
        </div>
      </footer>
    </div>
  );
}
